import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * A = 2 points
 * B = 1 point
 * C = 0 points
 */

const scoreMap = {
  0: 2,
  1: 1,
  2: 0,
};

const quizData = [
  {
    question: "When I feel physically or mentally tired, I usually…",
    options: [
      "Allow myself to rest without much guilt",
      "Push myself a bit more before resting",
      "Ignore it and keep going",
    ],
  },
  {
    question: "The way I speak to myself internally is usually…",
    options: [
      "Kind, encouraging, or understanding",
      "A mix of supportive and critical",
      "Mostly harsh or demanding",
    ],
  },
  {
    question: "When I need to say “no” to someone, I usually…",
    options: [
      "Say it respectfully and clearly",
      "Struggle but sometimes manage",
      "Avoid it and put others first",
    ],
  },
  {
    question: "My sense of self-worth feels…",
    options: [
      "Fairly stable, regardless of others’ opinions",
      "Influenced by how others respond to me",
      "Largely dependent on validation from others",
    ],
  },
  {
    question: "When I achieve something (big or small), I…",
    options: [
      "Acknowledge and appreciate myself",
      "Feel good briefly and move on quickly",
      "Rarely pause to appreciate it",
    ],
  },
  {
    question: "When I make a mistake, my natural response is to…",
    options: [
      "Forgive myself and learn from it",
      "Replay it in my head for some time",
      "Blame or shame myself",
    ],
  },
  {
    question: "I prioritise my own needs (rest, health, emotions)…",
    options: ["Regularly", "Occasionally", "Rarely"],
  },
  {
    question: "Being alone with myself feels…",
    options: [
      "Comfortable or nourishing",
      "Okay for short periods",
      "Uncomfortable or unsettling",
    ],
  },
  {
    question: "I honour my physical and emotional limits by…",
    options: [
      "Listening to them most of the time",
      "Noticing them but often crossing them",
      "Ignoring them",
    ],
  },
  {
    question: "Deep down, I believe that I deserve care, rest, and love…",
    options: [
      "Yes, without conditions",
      "Sometimes, but not always",
      "Rarely or only when I “earn” it",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */


const IntroPage = ({ onStart }) => (
  <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
    <div
      className="
        relative
        overflow-hidden
        rounded-[34px]
        max-w-5xl
        w-full
        h-[620px]
        shadow-[0_30px_80px_rgba(20,10,0,.12)]
      "
    >
      <img
        src="/assets/knowyourself/quiz/selflove.jpg"
        alt="Self Love Assessment"
        className="
          w-full
          h-full
          object-cover
          brightness-[.84]
          saturate-[.9]
        "
      />

      <div
        className="
          absolute
          inset-0
          bg-gradient-to-t
          from-black/90
          via-black/35
          to-transparent
        "
      />

      <div className="absolute inset-x-0 bottom-0 p-6">
        <div
          className="
            rounded-[28px]
            bg-white/10
            backdrop-blur-2xl
            border
            border-white/10
            p-8
          "
        >
          <div
            className="
              inline-flex
              px-4
              py-2
              rounded-full
              bg-white/10
              backdrop-blur-xl
              border
              border-white/10
              text-white
              text-xs
              tracking-[.08em]
              mb-5
            "
          >
            SELF ASSESSMENT
          </div>

          <h1
            className="
              text-[38px]
              md:text-[56px]
              leading-[1.05]
              text-white
              mb-5
            "
          >
            Self-Love Assessment
          </h1>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-8
            "
          >
            Explore how you relate to yourself through
            self-care, boundaries, self-worth,
            compassion, and emotional wellbeing.
          </p>

          <Button
            variant="primary"
            text="Start Assessment"
            onClick={onStart}
          />
        </div>
      </div>
    </div>
  </div>
);
/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans) => sum + scoreMap[ans],
    0
  );

  let title = "";
  let message = "";

  if (totalScore >= 15) {
    title = "💗 Healthy Self-Love";

    message =
      "You generally treat yourself with kindness, respect, and care. You are able to rest, set healthy boundaries, and validate your own worth without relying heavily on external approval. Continue nurturing this relationship with yourself.";
  } else if (totalScore >= 8) {
    title = "🌱 Growing Self-Love";

    message =
      "You are becoming more aware of self-care and self-respect, but guilt, perfectionism, or self-criticism may still appear from time to time. Focus on consistent boundaries, compassion, and kinder self-talk.";
  } else {
    title = "🌧️ Self-Love Needs Attention";

    message =
      "You may be neglecting your own needs or being overly critical of yourself. This is often the result of conditioning rather than personal failure. Start small with rest, self-compassion, and allowing yourself to receive care.";
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
      <div
        className="
          relative
          overflow-hidden
          rounded-[34px]
          max-w-5xl
          w-full
          h-[620px]
          shadow-[0_30px_80px_rgba(20,10,0,.12)]
        "
      >
        <img
          src="/assets/knowyourself/quiz/result.jpg"
          alt="Assessment Result"
          className="
            w-full
            h-full
            object-cover
            brightness-[.84]
            saturate-[.9]
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/90
            via-black/35
            to-transparent
          "
        />

        <div className="absolute inset-x-0 bottom-0 p-6">
          <div
            className="
              rounded-[28px]
              bg-white/10
              backdrop-blur-2xl
              border
              border-white/10
              p-8
            "
          >
            <div
              className="
                inline-flex
                px-4
                py-2
                rounded-full
                bg-white/10
                backdrop-blur-xl
                border
                border-white/10
                text-white
                text-xs
                tracking-[.08em]
                mb-5
              "
            >
              YOUR RESULT
            </div>

            <h2
              className="
                text-[34px]
                md:text-[52px]
                leading-[1.05]
                text-white
                mb-4
              "
            >
              {title}
            </h2>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-4
              "
            >
              Self-Love Score: {totalScore} / 20
            </p>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-8
                max-w-3xl
              "
            >
              {message}
            </p>

            <Button
              variant="primary"
              text="Retake Assessment"
              onClick={onRetake}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
/* -------------------- MAIN COMPONENT -------------------- */

const SelfLoveQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default SelfLoveQuiz;
